import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { subDays } from 'date-fns';

import ApiService from '../../services/apiService';
import {
    AvailableCarrierBusinessUnitsResponse,
    AvailableCommoditiesResponse,
    AvailableConsigneesResponse,
    AvailableEquipmentTypesResponse,
    AvailableShippersResponse,
} from '../../interfaces/services/catalog';
import { CarrierBusinessUnit, Commodity, DeliveryParty, EquipmentType, Filters, PickupParty } from '../../interfaces/filters';
import { formatDateForApi } from '../../utils/dateUtils';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { ApiStatus } from '../../utils/enums';

interface FilterState {
    filterPanelOpen: boolean;
    searchFilters: Filters;
    filterOptions: {
        shippers: PickupParty[];
        consignees: DeliveryParty[];
        commodities: Commodity[];
        equipmentTypes: EquipmentType[];
        carrierBusinessUnits: CarrierBusinessUnit[];
    };
    filterCarrierBusinessUnitsApiStatus: ApiStatus;
    filterCommoditiesApiStatus: ApiStatus;
    filterConsigneesApiStatus: ApiStatus;
    filterEquipmentTypesApiStatus: ApiStatus;
    filterShippersApiStatus: ApiStatus;
}

const now = subDays(new Date(), 1);
const prior7Date = subDays(new Date(), 7);
const categoryApiPath = '/utility/v2/sci/catalog';

const initialFilters: Filters = {
    earliest_delivery_departure_timestamp: formatDateForApi(prior7Date),
    latest_delivery_departure_timestamp: formatDateForApi(now),
    carrier_business_unit_codes: [],
    commodity_codes: [],
    pickup_party_ids: [],
    delivery_party_ids: [],
    freight_order_specified_eqpt_codes: [],
    minimum_revenue_usd_amount: null,
    maximum_revenue_usd_amount: null,
    cleanse_data: false,
};

const initialState: FilterState = {
    filterPanelOpen: false,
    searchFilters: initialFilters,
    filterOptions: {
        shippers: [],
        consignees: [],
        commodities: [],
        equipmentTypes: [],
        carrierBusinessUnits: [],
    },
    filterCarrierBusinessUnitsApiStatus: ApiStatus.Idle,
    filterCommoditiesApiStatus: ApiStatus.Idle,
    filterConsigneesApiStatus: ApiStatus.Idle,
    filterEquipmentTypesApiStatus: ApiStatus.Idle,
    filterShippersApiStatus: ApiStatus.Idle,
};

export const loadAvailableCarrierBusinessUnits = createAsyncThunk(
    'filters/loadAvailableCarrierBusinessUnits',
    async () => {
        try {
            const { data } = await ApiService.get({
                url: `${getTTCBaseUrl()}${categoryApiPath}/carrier-business-units`,
            }) as AxiosResponse<AvailableCarrierBusinessUnitsResponse>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve available carrier business units', error);
            toast.error('Failed to load available carrier business units');
            throw error;
        }
    }
);

export const loadAvailableCommodities = createAsyncThunk(
    'filters/loadAvailableCommodities',
    async () => {
        try {
            const { data } = await ApiService.get({
                url: `${getTTCBaseUrl()}${categoryApiPath}/commodities`,
            }) as AxiosResponse<AvailableCommoditiesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve available commodities', error);
            toast.error('Failed to load available commodities');
            throw error;
        }
    }
);

export const loadAvailableConsignees = createAsyncThunk(
    'filters/loadAvailableConsignees',
    async () => {
        try {
            const { data } = await ApiService.get({
                url: `${getTTCBaseUrl()}${categoryApiPath}/delivery-parties`,
            }) as AxiosResponse<AvailableConsigneesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve available consignees', error);
            toast.error('Failed to load available consignees');
            throw error;
        }
    }
);

export const loadAvailableEquipmentTypes = createAsyncThunk(
    'filters/loadAvailableEquipmentTypes',
    async () => {
        try {
            const { data } = await ApiService.get({
                url: `${getTTCBaseUrl()}${categoryApiPath}/equipment-types`,
            }) as AxiosResponse<AvailableEquipmentTypesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve available equipment types', error);
            toast.error('Failed to load available equipment types');
            throw error;
        }
    }
);

export const loadAvailableShippers = createAsyncThunk(
    'filters/loadAvailableShippers',
    async () => {
        try {
            const { data } = await ApiService.get({
                url: `${getTTCBaseUrl()}${categoryApiPath}/pickup-parties`,
            }) as AxiosResponse<AvailableShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve available shippers', error);
            toast.error('Failed to load available shippers');
            throw error;
        }
    }
);

const filterSlice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        openFilterPanel: (state) => {
            state.filterPanelOpen = true;
        },
        closeFilterPanel: (state) => {
            state.filterPanelOpen = false;
        },
        saveSearchFilters: (state, action: PayloadAction<Filters>) => {
            state.searchFilters = {
                ...state.searchFilters,
                ...action.payload,
            };
        },
        resetSearchFilters: (state) => {
            state.searchFilters = initialFilters;
        },
    },
    extraReducers(builder) {
        builder.addCase(loadAvailableCarrierBusinessUnits.pending, (state) => {
            state.filterCarrierBusinessUnitsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(loadAvailableCarrierBusinessUnits.fulfilled, (state, action) => {
            state.filterCarrierBusinessUnitsApiStatus = ApiStatus.Success;
            state.filterOptions.carrierBusinessUnits = action.payload.carrier_business_units;
        });
        builder.addCase(loadAvailableCarrierBusinessUnits.rejected, (state) => {
            state.filterCarrierBusinessUnitsApiStatus = ApiStatus.Failure;
        });
        builder.addCase(loadAvailableCommodities.pending, (state) => {
            state.filterCommoditiesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(loadAvailableCommodities.fulfilled, (state, action) => {
            state.filterCommoditiesApiStatus = ApiStatus.Success;
            state.filterOptions.commodities = action.payload.commodities;
        });
        builder.addCase(loadAvailableCommodities.rejected, (state) => {
            state.filterCommoditiesApiStatus = ApiStatus.Failure;
        });
        builder.addCase(loadAvailableConsignees.pending, (state) => {
            state.filterConsigneesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(loadAvailableConsignees.fulfilled, (state, action) => {
            state.filterConsigneesApiStatus = ApiStatus.Success;
            state.filterOptions.consignees = action.payload.delivery_parties;
        });
        builder.addCase(loadAvailableConsignees.rejected, (state) => {
            state.filterConsigneesApiStatus = ApiStatus.Failure;
        });
        builder.addCase(loadAvailableEquipmentTypes.pending, (state) => {
            state.filterEquipmentTypesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(loadAvailableEquipmentTypes.fulfilled, (state, action) => {
            state.filterEquipmentTypesApiStatus = ApiStatus.Success;
            state.filterOptions.equipmentTypes = action.payload.freight_order_specified_eqpt;
        });
        builder.addCase(loadAvailableEquipmentTypes.rejected, (state) => {
            state.filterEquipmentTypesApiStatus = ApiStatus.Failure;
        });
        builder.addCase(loadAvailableShippers.pending, (state) => {
            state.filterShippersApiStatus = ApiStatus.Loading;
        });
        builder.addCase(loadAvailableShippers.fulfilled, (state, action) => {
            state.filterShippersApiStatus = ApiStatus.Success;
            state.filterOptions.shippers = action.payload.pickup_parties;
        });
        builder.addCase(loadAvailableShippers.rejected, (state) => {
            state.filterShippersApiStatus = ApiStatus.Failure;
        });
    },
});

export default filterSlice.reducer;

export const {
    openFilterPanel,
    closeFilterPanel,
    saveSearchFilters,
    resetSearchFilters,
} = filterSlice.actions;
